// Tells a player their turn has come: a short chime, and while the tab is in
// the background a flashing "Your turn" in its title.
import { useEffect, useRef, useState } from 'react';

const STORE_KEY = 'turnSound';

let context;
const audio = () => {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) {
        return null;
    }
    if (!context) {
        context = new AudioContext();
    }
    return context;
};

// Browsers only let a page make sound after the player has clicked or tapped
// something, so wake the audio on the first touch.
if (typeof window !== 'undefined') {
    const unlock = () => {
        audio()?.resume?.().catch(() => {});
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
}

/** Two soft rising notes, drawn rather than loaded so there is no file to fetch. */
export const playChime = () => {
    const ctx = audio();
    if (!ctx || ctx.state === 'closed') {
        return;
    }
    ctx.resume?.().catch(() => {});
    const start = ctx.currentTime + 0.02;
    [[784, 0], [1047, 0.13]].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const t = start + delay;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.22, t + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + 0.5);
    });
};

const readOn = () => {
    try {
        return localStorage.getItem(STORE_KEY) !== 'off';
    } catch (error) {
        return true;
    }
};

/** The player's sound choice, kept between visits. On unless switched off. */
export const useTurnSound = () => {
    const [on, setOn] = useState(readOn);
    const toggle = () => setOn(current => {
        const next = !current;
        try {
            localStorage.setItem(STORE_KEY, next ? 'on' : 'off');
        } catch (error) { /* private mode */ }
        if (next) {
            playChime();
        }
        return next;
    });
    return [on, toggle];
};

/**
 * Chimes when `myTurn` turns true. Not on first load (you're already looking),
 * and against only computers just when the tab is in the background, as the
 * computer hands the turn back every couple of seconds.
 */
export const useTurnAlert = ({ myTurn, gameKey, soundOn, onlyBots }) => {
    const seen = useRef({ gameKey: null, myTurn: false });

    useEffect(() => {
        const last = seen.current;
        seen.current = { gameKey, myTurn };
        if (!gameKey || last.gameKey !== gameKey || !myTurn || last.myTurn) {
            return;
        }
        if (soundOn && (!onlyBots || document.hidden)) {
            playChime();
        }
    }, [myTurn, gameKey, soundOn, onlyBots]);

    // Flash the tab title while it's your turn and you're looking elsewhere.
    useEffect(() => {
        if (!myTurn) {
            return undefined;
        }
        let timer = null;
        let base = null;
        const stop = () => {
            clearInterval(timer);
            timer = null;
            if (base !== null) {
                document.title = base;
                base = null;
            }
        };
        const start = () => {
            if (timer || !document.hidden) {
                return;
            }
            base = document.title;
            let lit = false;
            timer = setInterval(() => {
                lit = !lit;
                document.title = lit ? '● Your turn · Play Sequence' : base;
            }, 1000);
        };
        const onVisibility = () => (document.hidden ? start() : stop());
        start();
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            stop();
        };
    }, [myTurn]);
};
